import React from 'react';
import { Lesson } from '../../hooks/useCourseApi';
import { cardTheme } from '../../styles/cardTheme';
import videoLessonIcon from '../../assets/images/curriculum/video.png';
import quizLessonIcon from '../../assets/images/curriculum/quiz.png';
import homeworkLessonIcon from '../../assets/images/curriculum/homework.png';
import pdfLessonIcon from '../../assets/images/curriculum/pdf.png';

interface UnitLessonTabsProps {
  unitName: string;
  lessons: Lesson[];
  activeIndex: number;
  completedIds: string[];
  onSelect: (lessonIndex: number) => void;
}

const lessonTypeMeta = (type: string) => {
  if (type === 'Video') return { label: 'فيديو', icon: videoLessonIcon };
  if (type === 'Quiz') return { label: 'اختبار', icon: quizLessonIcon };
  if (type === 'Assignment') return { label: 'واجب', icon: homeworkLessonIcon };
  if (type === 'File') return { label: 'ملف', icon: pdfLessonIcon };
  return { label: type, icon: videoLessonIcon };
};

const isLessonDone = (lesson: Lesson, completedIds: string[]) =>
  completedIds.includes(String(lesson.id)) ||
  (lesson.type === 'Video' && Boolean(lesson.isCompleted)) ||
  (lesson.type === 'Quiz' && lesson.isQuizSubmitted === true) ||
  (lesson.type === 'Assignment' && lesson.isAssignmentSubmitted === true) ||
  (lesson.type === 'File' && lesson.isFileOpened === true);

const UnitLessonTabs: React.FC<UnitLessonTabsProps> = ({
  unitName,
  lessons,
  activeIndex,
  completedIds,
  onSelect,
}) => {
  if (lessons.length === 0) return null;

  return (
    <section className={`${cardTheme.shell} mx-3 my-3 font-cairo sm:mx-4 sm:my-4`} dir="rtl">
      <div className="flex items-center justify-between gap-2 border-b border-green-100 px-3 py-2.5 sm:px-4">
        <h2 className="min-w-0 truncate text-sm font-bold text-green-900 sm:text-base">{unitName}</h2>
        <span className="shrink-0 text-[11px] font-semibold text-gold-700 sm:text-xs">
          {activeIndex + 1} / {lessons.length}
        </span>
      </div>

      <div role="tablist" aria-label="دروس الوحدة" className="flex flex-col">
        {lessons.map((lesson, index) => {
          const active = index === activeIndex;
          const done = isLessonDone(lesson, completedIds);
          const { label, icon } = lessonTypeMeta(lesson.type);
          return (
            <button
              key={lesson.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onSelect(index)}
              className={`flex min-h-12 w-full items-center gap-3 border-b border-green-50 px-3 py-2 text-right last:border-b-0 sm:px-4 ${
                active ? 'border-s-4 border-s-gold-500 bg-green-50' : 'bg-white hover:bg-green-50/70'
              }`}
            >
              <img src={icon} alt="" className="h-8 w-8 shrink-0 object-contain" />
              <span className={`min-w-0 flex-1 truncate text-sm font-bold ${active ? 'text-green-900' : 'text-green-800'}`}>
                {lesson.lessonName}
              </span>
              <span className={`shrink-0 text-[11px] font-semibold sm:text-xs ${done ? 'text-green-700' : 'text-gray-500'}`}>
                {done ? 'تم' : label}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default UnitLessonTabs;
